import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';

import StepFunctionExecutionsApi from '../../api/step_function_executions';
import buildReason from './buildReason';
import { StatusCounts, statusInk } from './StatusBreakdown';
import { statusLabel, subtitle, healthRowsPropType } from './shared';
import {
  Card,
  Scroll,
  Table,
  Row,
  IntgName,
  Pill,
  LastOk,
  Reason,
  Chev,
  DetailPad,
  ExRow,
  Skeleton,
} from './style';
import { formatTimestamp24h, timeAgo } from '../../utils/dates';

// The drill-down is a preview; the full payload lives in the execution modal.
// Trim so a huge error (e.g. an HTML error page) can't bloat the DOM or the row.
const MAX_DETAIL_LENGTH = 160;
const trim = (str) =>
  str.length > MAX_DETAIL_LENGTH ? `${str.slice(0, MAX_DETAIL_LENGTH)}…` : str;

const execDetail = (attributes = {}) => {
  const e = attributes.error;
  if (!e) return '—';
  if (typeof e === 'string') return trim(e);
  if (Array.isArray(e)) return trim(e[0]?.title || e[0]?.message || '—');
  return trim(e.message || e.title || e.detail || '—');
};

function SkeletonRows() {
  const widths = ['46%', '28%', '24%', '26%', '58%', '6%'];
  return [0, 1, 2, 3].map((r) => (
    <tr key={r}>
      {widths.map((w, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <td key={i}>
          <Skeleton style={{ width: w }} />
        </td>
      ))}
    </tr>
  ));
}

export default function HealthTable({ rows, loading, emptyState }) {
  const [open, setOpen] = useState({});
  const [exec, setExec] = useState({});
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const loadExec = useCallback(async (id) => {
    setExec((prev) => ({ ...prev, [id]: { loading: true } }));
    const res =
      await StepFunctionExecutionsApi.fetchStepFunctionExecutionsByIntegrationId(
        {
          integrationId: id,
          source: 'LIVE',
          limit: 5,
        },
      );
    if (!mounted.current) return;
    setExec((prev) => ({
      ...prev,
      [id]: {
        loading: false,
        list: res.data || [],
        error: Boolean(res.errors),
      },
    }));
  }, []);

  const toggle = (id) => {
    const willOpen = !open[id];
    setOpen((prev) => ({ ...prev, [id]: willOpen }));
    if (willOpen && !exec[id]) loadExec(id);
  };

  const renderDetail = (id) => {
    const state = exec[id];
    if (!state || state.loading)
      return (
        <ExRow>
          <span className="muted">{t`Loading recent executions…`}</span>
        </ExRow>
      );
    if (state.error)
      return (
        <ExRow>
          <span className="muted">{t`Couldn’t load executions.`}</span>
        </ExRow>
      );
    if (!state.list.length)
      return (
        <ExRow>
          <span className="muted">{t`No recent LIVE executions.`}</span>
        </ExRow>
      );
    return state.list.map((ex) => {
      const a = ex.attributes || {};
      return (
        <ExRow key={ex.id}>
          {/* The helper returns '' for anything it cannot parse, so the
              fallback belongs on its result, not on the raw value. */}
          <span>{formatTimestamp24h(a.start_date) || '—'}</span>
          <span className="st" style={{ color: statusInk(a.status) }}>
            {a.status}
          </span>
          <span className="detail">{execDetail(a)}</span>
        </ExRow>
      );
    });
  };

  return (
    <Card>
      <Scroll>
        <Table>
          <thead>
            <tr>
              <th>{t`Integration`}</th>
              <th>{t`Status`}</th>
              <th>{t`Outcomes · last 6h`}</th>
              <th>{t`Last success`}</th>
              <th>{t`Reason`}</th>
              <th aria-label={t`Executions`} />
            </tr>
          </thead>
          <tbody>
            {loading && <SkeletonRows />}
            {!loading && !rows.length && (
              <tr>
                <td colSpan={6}>{emptyState}</td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => {
                const a = r.attributes || {};
                const isOpen = Boolean(open[r.id]);
                return (
                  <React.Fragment key={r.id}>
                    <Row
                      className={`sev-${a.status} expandable`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onClick={() => toggle(r.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggle(r.id);
                        }
                      }}
                    >
                      <td>
                        <IntgName>
                          <a
                            href={`/integrations/${r.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {a.name}
                          </a>
                          <span className="ext">↗</span>
                        </IntgName>
                        <span className="sub">{subtitle(a)}</span>
                      </td>
                      <td>
                        <Pill className={a.status}>
                          <span className="d" />
                          {statusLabel(a.status)}
                        </Pill>
                      </td>
                      <td>
                        <StatusCounts attributes={a} />
                      </td>
                      <td>
                        <LastOk
                          className={a.status === 'unhealthy' ? 'stale' : ''}
                        >
                          {a.last_success_at
                            ? timeAgo(a.last_success_at)
                            : t`Never`}
                        </LastOk>
                      </td>
                      <td>
                        <Reason>{buildReason(a)}</Reason>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Chev className={isOpen ? 'open' : ''}>›</Chev>
                      </td>
                    </Row>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <DetailPad>
                            <h4>{t`Recent executions`}</h4>
                            {renderDetail(r.id)}
                          </DetailPad>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </Table>
      </Scroll>
    </Card>
  );
}

HealthTable.propTypes = {
  rows: healthRowsPropType.isRequired,
  loading: PropTypes.bool.isRequired,
  emptyState: PropTypes.node.isRequired,
};
